import { useState, useEffect } from 'react';
import { ArrowRight, Check, ChevronRight, Terminal, Shield, Zap, Maximize, Activity, HelpCircle, FileText, Globe, Layers, MousePointer2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// --- Components ---

const ParallaxCard = () => {
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const centerX = box.width / 2;
    const centerY = box.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => setRotate({ x: 0, y: 0 });

  return (
    <div
      className="perspective-1000 relative w-full h-[300px] sm:h-[400px] flex items-center justify-center cursor-pointer px-4"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative w-64 h-40 sm:w-80 sm:h-52 transition-transform duration-200 ease-out preserve-3d"
        style={{ transform: `rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)` }}
      >
        {/* Shadow */}
        <div className="absolute top-10 left-10 w-full h-full bg-black/20 blur-2xl rounded-2xl transform translate-z-[-20px]"></div>

        {/* Card Body */}
        <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden p-6 flex flex-col justify-between transform translate-z-[20px]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-bl-full -mr-10 -mt-10"></div>

          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="w-8 h-8 rounded bg-gray-900"></div>
              <div className="w-24 h-2 bg-gray-200 rounded"></div>
            </div>
            <div className="w-12 h-12 border border-gray-100 rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-gray-900/5 p-1">
                <div className="w-full h-full bg-black"></div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="w-full h-2 bg-gray-100 rounded"></div>
            <div className="w-2/3 h-2 bg-gray-100 rounded"></div>
            <div className="w-1/2 h-2 bg-gray-100 rounded"></div>
          </div>

          <div className="flex justify-between items-end">
            <div className="font-mono text-xs text-gray-400">ID: 8839-2992</div>
            <div className="px-2 py-1 bg-green-50 text-green-600 text-[10px] font-bold uppercase rounded">Verified</div>
          </div>
        </div>

        {/* Floating Elements (3D Effect) */}
        <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-black text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded text-[10px] sm:text-xs font-mono shadow-lg transform translate-z-[50px]">
          Verified
        </div>
        <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 bg-white border border-gray-200 text-gray-900 px-3 py-1.5 sm:px-4 sm:py-2 rounded text-[10px] sm:text-xs font-mono shadow-lg transform translate-z-[30px] flex items-center gap-1.5 sm:gap-2">
          <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-500 fill-yellow-500" />
          Instant
        </div>
      </div>
    </div>
  );
};

const VerticalTextSlider = () => {
  return (
    <div className="h-[1.1em] overflow-hidden inline-block align-top min-w-[200px] sm:min-w-[250px] md:min-w-[300px]">
      <div className="animate-slide-up">
        <div className="h-[1.1em] flex items-center text-gray-400">Identity</div>
        <div className="h-[1.1em] flex items-center text-blue-600">Aadhaar</div>
        <div className="h-[1.1em] flex items-center text-green-600">Voter ID</div>
        <div className="h-[1.1em] flex items-center text-purple-600">PAN Card</div>
        <div className="h-[1.1em] flex items-center text-gray-400">Identity</div>
      </div>
    </div>
  );
};

const LandingPageNew = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('specifications');
  const [toastMessage, setToastMessage] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const navigate = useNavigate();

  const handleComingSoon = (e) => {
    e.preventDefault();
    setToastMessage('Coming Soon');
    setTimeout(() => setToastMessage(''), 3000);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  const handleGetStarted = () => {
    navigate(user ? '/home' : '/login');
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-black selection:text-white">
      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .translate-z-\[-20px\] { transform: translateZ(-20px); }
        .translate-z-\[20px\] { transform: translateZ(20px); }
        .translate-z-\[30px\] { transform: translateZ(30px); }
        .translate-z-\[50px\] { transform: translateZ(50px); }
        
        @keyframes slide-up {
          0%, 20% { transform: translateY(0); }
          25%, 45% { transform: translateY(-20%); }
          50%, 70% { transform: translateY(-40%); }
          75%, 95% { transform: translateY(-60%); }
          100% { transform: translateY(-80%); }
        }
        .animate-slide-up {
          animation: slide-up 8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }

        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 40s linear infinite;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        @media (max-width: 640px) {
          .xs\:inline { display: inline; }
          .xs\:hidden { display: none; }
          .xs\:hidden { display: none; }
        }
        html { scroll-behavior: smooth; }
      `}</style>

      <div className="border-x border-gray-200 max-w-[1440px] mx-auto bg-white shadow-2xl relative">

        {/* Header */}
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/logo192.png" alt="SB Cards" className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg" />
            <span className="font-bold text-base sm:text-lg tracking-tight hidden xs:inline">SB Cards</span>
            <span className="font-bold text-base tracking-tight xs:hidden">SB</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs border border-gray-200 rounded-full px-3 py-1">
              <span className="font-semibold text-gray-700">v2.4</span>
              <span className="text-gray-400">•</span>
              <span className="text-green-600 font-medium">Latest</span>
            </div>
            <button onClick={handleGetStarted} className="bg-black text-white px-3 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-medium hover:bg-gray-800 transition-colors rounded-lg">
              {user ? 'Go to Dashboard' : 'Get Started'}
            </button>
          </div>
        </header>

        {/* Hero */}
        <div className="grid lg:grid-cols-2 min-h-[70vh] sm:min-h-[80vh] border-b border-gray-200">
          <div className="p-6 sm:p-8 md:p-12 lg:p-20 flex flex-col justify-center lg:border-r border-gray-200 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(#f5f5f5_1px,transparent_1px),linear-gradient(90deg,#f5f5f5_1px,transparent_1px)] bg-[size:20px_20px] -z-10"></div>

            <div className="inline-flex mb-6 sm:mb-8 items-center gap-2 text-xs sm:text-sm font-medium text-gray-500 flex-wrap">
              <span className="text-xs sm:text-sm">Create cards in seconds, not hours</span>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-semibold tracking-tighter leading-[0.9] mb-6 sm:mb-8 md:mb-12">
              Generate <br />
              <VerticalTextSlider /> <br />
              Instantly.
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-500 max-w-md mb-6 sm:mb-8 md:mb-12 leading-relaxed">
              Create professional, government-compliant identity cards in seconds. Secure, accurate, and print-ready.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={handleGetStarted}
                className="bg-black text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors rounded-lg"
              >
                {user ? 'Go to Dashboard' : 'Get Started Free'} <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 flex items-center justify-center relative overflow-hidden min-h-[300px] sm:min-h-[400px] lg:min-h-0 py-8 sm:py-12 lg:py-0 border-t lg:border-t-0 border-gray-200">
            <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
              <div className="text-[120px] sm:text-[200px] font-bold">ID</div>
            </div>
            <ParallaxCard />
          </div>
        </div>

        {/* Feature Highlights Strip */}
        <div className="border-b border-gray-200 py-4 sm:py-6 bg-white">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 px-4">
            {[
              { label: "Aadhaar Cards", desc: "with QR & Hindi" },
              { label: "Voter ID", desc: "EPIC format" },
              { label: "PAN Cards", desc: "with signature" },
              { label: "Sok Sandesh", desc: "custom design" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="font-medium">{item.label}</span>
                <span className="text-gray-400 hidden sm:inline">- {item.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Section - "Why Us" */}
        <div id="comparison" className="grid md:grid-cols-12 border-b border-gray-200">
          <div className="md:col-span-5 p-6 sm:p-8 md:p-12 lg:p-16 md:border-r border-gray-200">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight mb-4 sm:mb-6">Why Choose SB Cards?</h2>
            <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8">Streamline your card creation workflow. From data entry to print-ready PDFs in seconds, we handle the complexity so you don't have to.</p>

            <div className="space-y-3 sm:space-y-4">
              <div className="p-3 sm:p-4 bg-gray-50 rounded border border-gray-100">
                <div className="text-xs sm:text-sm text-gray-400 mb-1">Average Generation Time</div>
                <div className="flex justify-between items-end gap-2">
                  <div className="text-xl sm:text-2xl font-bold">&lt; 30 Seconds</div>
                  <div className="text-green-600 text-xs sm:text-sm font-medium">Fast Export</div>
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-gray-50 rounded border border-gray-100">
                <div className="text-xs sm:text-sm text-gray-400 mb-1">Print Quality</div>
                <div className="flex justify-between items-end gap-2">
                  <div className="text-xl sm:text-2xl font-bold">300 DPI</div>
                  <div className="text-green-600 text-xs sm:text-sm font-medium">Professional Grade</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7 bg-white p-6 sm:p-8 md:p-12 lg:p-16 border-t md:border-t-0 border-gray-200">
            {/* Comparison Table */}
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="p-2 sm:p-4 font-medium text-gray-500">Feature</th>
                    <th className="p-2 sm:p-4 font-bold">SB Cards</th>
                    <th className="p-2 sm:p-4 font-medium text-gray-400">Manual / Others</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { feat: 'Generation Time', us: 'Under 30 seconds', them: '15-30 Minutes' },
                    { feat: 'Hindi Translation', us: 'Automatic', them: 'Manual Typing' },
                    { feat: 'QR Code', us: 'Generated', them: 'Static Image' },
                    { feat: 'Format', us: 'PDF (Print Ready)', them: 'Image File' },
                    { feat: 'Privacy', us: 'Browser-Only', them: 'Server Upload' }
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="p-2 sm:p-4 text-gray-500">{row.feat}</td>
                      <td className="p-2 sm:p-4 font-medium">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                          <span className="break-words">{row.us}</span>
                        </div>
                      </td>
                      <td className="p-2 sm:p-4 text-gray-400">{row.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Interactive Tabs Section */}
        <div id="features" className="border-b border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide px-4 sm:px-6 md:px-10 lg:px-16">
            {['Specifications', 'Security', 'Integration', 'Support'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                className={`px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.toLowerCase()
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-400 hover:text-black'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6 sm:p-8 md:p-12 lg:p-20 bg-gray-50/50 min-h-[300px]">
            {activeTab === 'specifications' && (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 animate-fade-in">
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="font-bold flex items-center gap-2 text-sm sm:text-base"><Maximize className="w-4 h-4" /> Layout Engine</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Pixel-perfect layout engine that respects official government dimensions (85.60 × 53.98 mm) with proper bleed margins, ensuring standard-compliant prints.
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="font-bold flex items-center gap-2 text-sm sm:text-base"><Globe className="w-4 h-4" /> Localization</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Built-in transliteration API supports 12+ Indian languages with focus on high-accuracy Hindi conversion, making it accessible across regions.
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <h3 className="font-bold flex items-center gap-2 text-sm sm:text-base"><FileText className="w-4 h-4" /> Output</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Exports to industry-standard PDF with CMYK color profile support for thermal printers, guaranteeing professional-grade physical cards.
                  </p>
                </div>
              </div>
            )}
            {activeTab === 'security' && (
              <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 animate-fade-in">
                <div className="bg-white p-5 sm:p-6 lg:p-8 border border-gray-200 rounded-lg shadow-sm">
                  <div className="w-10 h-10 bg-green-50 text-green-600 rounded flex items-center justify-center mb-4 sm:mb-6">
                    <Shield className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold mb-2 text-sm sm:text-base">Your Data Stays With You</h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    All card generation happens directly in your browser. Your photos, Aadhaar numbers, and personal details are never uploaded to any server.
                  </p>
                </div>
                <div className="bg-white p-5 sm:p-6 lg:p-8 border border-gray-200 rounded-lg shadow-sm">
                  <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded flex items-center justify-center mb-4 sm:mb-6">
                    <Terminal className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold mb-2 text-sm sm:text-base">Local QR Generation</h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    QR codes are created on your device using the information you provide. Nothing leaves your computer until you download the final PDF.
                  </p>
                </div>
              </div>
            )}
            {activeTab === 'integration' && (
              <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 animate-fade-in">
                <div className="bg-white p-5 sm:p-6 lg:p-8 border border-gray-200 rounded-lg shadow-sm">
                  <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded flex items-center justify-center mb-4 sm:mb-6">
                    <Layers className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold mb-2 text-sm sm:text-base">Works Everywhere</h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Use on any modern browser - Chrome, Firefox, Safari, or Edge. No software installation needed. Works on desktop, laptop, and tablets.
                  </p>
                </div>
                <div className="bg-white p-5 sm:p-6 lg:p-8 border border-gray-200 rounded-lg shadow-sm">
                  <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded flex items-center justify-center mb-4 sm:mb-6">
                    <Activity className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold mb-2 text-sm sm:text-base">Export Options</h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Download your cards as high-quality PDFs ready for professional printing. Compatible with standard PVC card printers.
                  </p>
                </div>
              </div>
            )}
            {activeTab === 'support' && (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 animate-fade-in">
                <div className="space-y-3 sm:space-y-4 text-center sm:text-left">
                  <div className="w-10 h-10 bg-red-50 text-red-600 rounded flex items-center justify-center mb-3 sm:mb-4 mx-auto sm:mx-0">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-base">Email Support</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Have questions or facing issues? Reach out via email and we'll get back to you as soon as possible.
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-4 text-center sm:text-left">
                  <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded flex items-center justify-center mb-3 sm:mb-4 mx-auto sm:mx-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-base">Step-by-Step Guides</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Each card type has detailed instructions to help you fill in the correct information and generate perfect cards.
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-4 text-center sm:text-left">
                  <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded flex items-center justify-center mb-3 sm:mb-4 mx-auto sm:mx-0">
                    <MousePointer2 className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-sm sm:text-base">Easy to Use</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Simple form-based interface that anyone can use. No technical knowledge required - just fill in the details and download.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pricing / Card Types Section */}
        <div id="pricing" className="p-6 sm:p-8 md:p-12 lg:p-20 border-b border-gray-200 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-3 sm:mb-4">Simple, Transparent Pricing</h2>
              <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto">
                Pay only for what you use. Each card generation costs 25 points. No hidden fees, no subscriptions.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="p-4 sm:p-6 font-bold text-gray-900 text-sm sm:text-base">Card Type</th>
                      <th className="p-4 sm:p-6 font-bold text-gray-900 text-sm sm:text-base text-center">Features</th>
                      <th className="p-4 sm:p-6 font-bold text-gray-900 text-sm sm:text-base text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { name: "Aadhaar Card", features: "QR Code, Hindi Translation, Photo Upload" },
                      { name: "Voter ID Card", features: "Photo Upload, Multi-language Support" },
                      { name: "PAN Card", features: "QR Code, Signature Upload, PDF Export" },
                      { name: "Sok Sandesh Card", features: "Custom Design, Photo Upload" }
                    ].map((card, i) => (
                      <tr key={i} className="hover:bg-blue-50/30 transition-colors group">
                        <td className="p-4 sm:p-6">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">
                              {card.name[0]}
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">{card.name}</span>
                          </div>
                        </td>
                        <td className="p-4 sm:p-6 text-center">
                          <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">{card.features}</span>
                          <span className="text-xs text-gray-500 md:hidden">Multiple features</span>
                        </td>
                        <td className="p-4 sm:p-6 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xl sm:text-2xl font-bold text-black">25</span>
                            <span className="text-xs sm:text-sm text-gray-400">points</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Info Bar */}
              <div className="bg-gray-50 border-t border-gray-200 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span>New users get 100 free points - that's 4 free cards!</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 text-center">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-2">100</div>
                <div className="text-xs sm:text-sm text-gray-500">Free Points on Signup</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 text-center">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-2">4 Types</div>
                <div className="text-xs sm:text-sm text-gray-500">Cards Available</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 text-center">
                <div className="text-2xl sm:text-3xl font-bold text-black mb-2">PDF</div>
                <div className="text-xs sm:text-sm text-gray-500">Print-Ready Output</div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div id="faq" className="p-6 sm:p-8 md:p-12 lg:p-20 border-b border-gray-200">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-8 sm:mb-12 text-center">Frequently asked questions</h2>
            <div className="space-y-4 sm:space-y-6">
              {[
                {
                  q: 'How many points does it cost to generate a card?',
                  a: 'Each card generation costs 25 points, regardless of the card type (Aadhaar, Voter ID, PAN, or Sok Sandesh). New users receive 100 free points upon signup to get started.'
                },
                {
                  q: 'Is my personal data safe when using this tool?',
                  a: 'Yes, absolutely. All data processing happens directly in your browser (client-side). Your personal information like Aadhaar numbers, photos, and addresses are never sent to or stored on our servers. Once you close the tab, the data is gone.'
                },
                {
                  q: 'What card types can I generate?',
                  a: 'Currently, we support 4 card types: Aadhaar Card (with QR code and Hindi translation), Voter ID Card (EPIC format), PAN Card (with signature support), and Sok Sandesh Card (custom greeting cards).'
                },
                {
                  q: 'Can I print these cards on PVC plastic cards?',
                  a: 'Yes! Our PDF output is specifically formatted for CR-80 standard (85.60 × 53.98 mm) which is compatible with thermal PVC card printers. The output includes proper bleed margins for professional printing.'
                },
                {
                  q: 'How does the Hindi translation work?',
                  a: 'We use an advanced AI-powered transliteration engine that automatically converts your English text to Hindi (Devanagari script). This is especially useful for Aadhaar cards that require bilingual text.'
                },
                {
                  q: 'How can I get more points?',
                  a: 'Points can be purchased through our admin system. Contact our support team for bulk point purchases. Enterprise users can also request custom pricing plans for high-volume card generation needs.'
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="cursor-pointer border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors"
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                >
                  <div className="flex justify-between items-center py-3 sm:py-4 px-4 sm:px-5 gap-4 bg-white">
                    <h3 className={`font-medium transition-colors text-sm sm:text-base ${openFaqIndex === i ? 'text-blue-600' : 'text-gray-900'}`}>{item.q}</h3>
                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 flex-shrink-0 ${openFaqIndex === i ? 'rotate-90 text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 sm:px-5 pb-4 text-xs sm:text-sm text-gray-500 bg-gray-50 pt-3 border-t border-gray-100">
                      {item.a}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-50 p-6 sm:p-8 md:p-12 lg:p-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="font-bold text-base sm:text-lg mb-3 sm:mb-4">SB Cards</div>
              <div className="text-xs text-gray-400 mb-3 sm:mb-4">
                © 2026 SB Cards • Developed by SB Devs
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Product</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-500">
                <li><a href="#features" className="hover:text-black">Features</a></li>
                <li><a href="#comparison" className="hover:text-black">Comparison</a></li>
                <li><a href="#pricing" className="hover:text-black">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Resources</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-500">
                <li><a href="#" onClick={handleComingSoon} className="hover:text-black">Support</a></li>
                <li><a href="#" onClick={handleComingSoon} className="hover:text-black">Documentation</a></li>
                <li><a href="#" onClick={handleComingSoon} className="hover:text-black">API Reference</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">Legal</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-500">
                <li><Link to="/contact" className="hover:text-black">Contact Us</Link></li>
                <li><Link to="/terms" className="hover:text-black">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-black">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
        </footer>

        {/* Toast Notification */}
        {toastMessage && (
          <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
            <div className="bg-black text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <span className="font-medium">{toastMessage}</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default LandingPageNew;
